import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/payFileCancel.mutation"

import type {
  CancelPayFileMutation,
  CancelPayFileMutationVariables,
  PayFileCancelFailure,
  PayFileCancelInput,
} from "@/graphql/codegen/graphql"
import { CancelPayFileDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseCancelPayFileReturn {
  cancelPayFile: (input: PayFileCancelInput) => Promise<boolean>
  isCancelling: boolean
}

export const useCancelPayFile = (): UseCancelPayFileReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<CancelPayFileMutation, CancelPayFileMutationVariables>(
    CancelPayFileDocument
  )

  const cancelPayFile = useCallback(
    async (input: PayFileCancelInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.payFile.cancel
      const hasError = error || output?.__typename === "PayFileCancelFailure"

      if (hasError) {
        const message = error?.message ?? (output as PayFileCancelFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Pay file cancelled" })
      return true
    },
    [executeMutation, notify]
  )

  return { cancelPayFile, isCancelling: fetching }
}
